import { createRef, useEffect, useRef, useState } from 'react';

import { Button } from '@capsule/consumer-design-system';

import type { ArrayHelpers } from 'formik';
import { FieldArray, useFormikContext } from 'formik';
import { AnimatePresence } from 'framer-motion';

import { DrugSearchAutoComplete, getExtrasField } from 'src/app/components/Form/ApiAutoComplete/DrugSearch/container';
import CheckboxInput from 'src/app/components/Form/CheckBoxInput';
import { InputAnimation } from 'src/app/components/Pages/Checkout/MedicalHistory/styles';
import type { MedicationProviderComboboxFormValues } from 'src/app/types/medicalHistoryForm';
import { getToChildDownshiftInput } from 'src/app/utils/formUtils';

import { getMedicationFormValueName } from '../../PageSections/DoctorTransferForm/utils';

import { Container, ButtonContainer } from './styles';

const FIELD_CLEARED_IF_NOT_USED_AFTER = 250;

export interface MedicationProviderFieldsProps {
  /**
   * @default false
   */
  hasSkipCheckbox?: boolean;
}

export const MedicationProviderFields = ({ hasSkipCheckbox = false }: MedicationProviderFieldsProps) => {
  const {
    values: { medications, doctors, extras, noMedicationsCheckbox },
    setFieldValue,
    setFieldTouched,
  } = useFormikContext<MedicationProviderComboboxFormValues>();

  const lastMedication = medications?.at(-1) ?? '';
  const nullRef = useRef(null);
  const [refs, setRefs] = useState([nullRef]);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  // Creating a ref for every medication. This ref is used to focus or delete blank medication fields.
  useEffect(() => {
    setRefs(medications?.map(() => createRef()));
  }, [medications]);

  const handleAutomaticDeletion = ({
    index,
    medicationName,
    removeMedication,
  }: {
    index: number;
    removeMedication: ArrayHelpers['remove'];
    medicationName: string;
  }) => {
    setTimeout(() => {
      if (refs[index]?.current) {
        const medicationFieldRef = refs[index];
        const medicationField = getToChildDownshiftInput(medicationFieldRef);

        const isInputEmpty = !medicationName;
        const hasMultipleMedicationFields = refs.length > 1;
        const isCurrentMedicationFieldActive = medicationField === document.activeElement;

        if (isInputEmpty && hasMultipleMedicationFields && !isCurrentMedicationFieldActive) {
          removeMedication(index);

          if (doctors) {
            const updatedDoctors = doctors.filter((_, i) => i !== index);
            setFieldValue('doctors', updatedDoctors);
          }

          if (extras) {
            setFieldValue(
              'extras',
              [...extras].filter((_e, i) => i !== index)
            );
          }
        }
      }
    }, FIELD_CLEARED_IF_NOT_USED_AFTER);
  };

  const handleAddMedication = (
    pushMedication: (medicationName: MedicationProviderComboboxFormValues['medications'][0]) => void,
    medicationName: MedicationProviderComboboxFormValues['medications'][0]
  ) => {
    if (!medicationName) {
      // The last medication is always the 0 element in the refs array
      const medicationFieldRef = refs[0];
      getToChildDownshiftInput(medicationFieldRef)?.focus();
    } else {
      pushMedication('');

      if (doctors) {
        setFieldValue('doctors', [...doctors, '']);
      }
    }
  };

  return (
    <FieldArray name="medications">
      {({ push, remove }) => {
        return (
          <AnimatePresence initial={false}>
            {medications?.map((medication, index) => {
              const medicationName = getMedicationFormValueName(medication);
              const medicationIndex = `medications.${index}`;

              const isFilled = !!medicationName;
              const isFocused = focusedIndex === index;
              const isFirstInput = index === 0;
              const shouldUpperCase = isFilled || isFocused;

              // TODO: Figure out why CSS uppercase text transform for label of the TextInput isn't working for DrugSearchAutoComplete
              let label = 'Medication Name';
              if (shouldUpperCase) label = label.toUpperCase();
              if (!isFirstInput && !shouldUpperCase) label = 'Another medication (optional)';

              return (
                <Container key={index} ref={refs[index]}>
                  <InputAnimation>
                    <DrugSearchAutoComplete
                      compactStyle
                      textInputProps={{
                        name: medicationIndex,
                        id: medicationIndex,
                        ...getExtrasField(index),
                        type: 'text',
                        label,
                        'data-cy': `medication_name_${index}`,
                        onBlur: () => {
                          setFocusedIndex(-1);
                          handleAutomaticDeletion({ index, removeMedication: remove, medicationName });
                        },
                        onFocus: () => setFocusedIndex(index),
                        disabled: noMedicationsCheckbox,
                      }}
                    />
                  </InputAnimation>
                </Container>
              );
            })}

            <ButtonContainer hasOneFieldSet={medications?.length === 1}>
              {hasSkipCheckbox && !medications?.[0] ? (
                <CheckboxInput
                  hasBlueSentenceLabel
                  name="noMedicationsCheckbox"
                  label="I don’t have other medications"
                  onChange={() => {
                    return medications?.forEach((med, index) => {
                      if (!med) {
                        setFieldTouched(`medications.${index}`, false, false);
                      }
                    });
                  }}
                />
              ) : (
                <Button
                  variant="text-sm"
                  onClick={e => {
                    e.preventDefault();
                    handleAddMedication(push, lastMedication);
                  }}
                >
                  + Add Another Medication
                </Button>
              )}
            </ButtonContainer>
          </AnimatePresence>
        );
      }}
    </FieldArray>
  );
};
